import React, { useEffect, useMemo, useState } from "react"
import { listAdminTickets } from "../../services/tickets"
import { Link } from "react-router-dom"
import {
    TicketIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
} from "@heroicons/react/24/outline"

const AdminTickets = () => {
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState("")
    const [query, setQuery] = useState("")
    const [typeFilter, setTypeFilter] = useState("ALL") // ALL, EVENT, OFFER

    const fetchItems = async () => {
        setLoading(true)
        setError("")
        try {
            const res = await listAdminTickets()
            setLoading(false)
            if (res.status === 200) {
                setItems(Array.isArray(res.data) ? res.data : [])
            } else {
                setError(res.error || 'Failed to load tickets')
            }
        } catch (err) {
            setLoading(false)
            setError('An unexpected error occurred')
        }
    }

    useEffect(() => { fetchItems() }, [])

    const filtered = useMemo(() => {
        let result = items

        // Filter by ticket type
        if (typeFilter !== 'ALL') {
            result = result.filter(t => {
                if (typeFilter === 'EVENT') {
                    return t.eventId && !t.offerId
                } else if (typeFilter === 'OFFER') {
                    return t.offerId && !t.eventId
                }
                return true
            })
        }

        // Filter by search query
        const q = query.trim().toLowerCase()
        if (q) {
            result = result.filter(t => (
                (t.code || '').toLowerCase().includes(q) ||
                (t.eventId?.title || '').toLowerCase().includes(q) ||
                (t.offerId?.title || '').toLowerCase().includes(q) ||
                (t.attendeeId?.name || '').toLowerCase().includes(q) ||
                (t.attendeeId?.email || '').toLowerCase().includes(q) ||
                (t.ticketTypeId?.name || '').toLowerCase().includes(q)
            ))
        }

        return result
    }, [items, query, typeFilter])

    return (
        <div className="max-w-7xl mx-auto">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-slate-900">Tickets</h1>
                <p className="text-slate-500 mt-1">Global ticket registry</p>
            </div>

            <div className="mb-6 flex flex-col sm:flex-row gap-4 justify-between items-center">
                <div className="flex flex-col sm:flex-row gap-4 flex-1">
                    <div className="relative w-full sm:max-w-md">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by code, event, offer, or attendee..."
                            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent bg-white shadow-sm"
                        />
                    </div>
                    <div className="relative">
                        <FunnelIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <select
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            className="pl-10 pr-8 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent bg-white shadow-sm appearance-none cursor-pointer"
                        >
                            <option value="ALL">All Tickets</option>
                            <option value="EVENT">Event Tickets</option>
                            <option value="OFFER">Offer Tickets</option>
                        </select>
                    </div>
                </div>
                <button onClick={fetchItems} className="px-4 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition">
                    Refresh
                </button>
            </div>

            {loading ? (
                <div className="text-center py-20">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
                    <p className="mt-2 text-slate-500">Loading tickets...</p>
                </div>
            ) : error ? (
                <div className="p-4 rounded-xl bg-red-50 text-red-600 border border-red-100 text-center">
                    {error}
                    <button onClick={fetchItems} className="block mx-auto mt-2 text-sm font-bold hover:underline">Try Again</button>
                </div>
            ) : (
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="px-6 py-4 font-semibold text-slate-700">Ticket Code</th>
                                    <th className="px-6 py-4 font-semibold text-slate-700">Event/Offer</th>
                                    <th className="px-6 py-4 font-semibold text-slate-700">Ticket Type</th>
                                    <th className="px-6 py-4 font-semibold text-slate-700">Attendee</th>
                                    <th className="px-6 py-4 font-semibold text-slate-700">Status</th>
                                    <th className="px-6 py-4 font-semibold text-slate-700 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-12 text-center text-slate-500">
                                            No tickets found.
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map(ticket => {
                                        const isEventTicket = ticket.eventId && !ticket.offerId
                                        const isOfferTicket = ticket.offerId && !ticket.eventId
                                        
                                        return (
                                            <tr key={ticket._id} className="hover:bg-slate-50/50 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-2">
                                                        <TicketIcon className="w-4 h-4 text-slate-400" />
                                                        <span className="font-mono font-medium text-slate-900">{ticket.code}</span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex flex-col">
                                                        {isEventTicket && (
                                                            <>
                                                                <span className="text-xs text-cyan-600 font-medium mb-1">EVENT</span>
                                                                <span className="text-slate-900 font-medium">{ticket.eventId?.title || 'Unknown Event'}</span>
                                                            </>
                                                        )}
                                                        {isOfferTicket && (
                                                            <>
                                                                <span className="text-xs text-purple-600 font-medium mb-1">OFFER</span>
                                                                <span className="text-slate-900 font-medium">{ticket.offerId?.title || 'Unknown Offer'}</span>
                                                            </>
                                                        )}
                                                        {!isEventTicket && !isOfferTicket && (
                                                            <span className="text-slate-500 text-sm">Unknown</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-slate-600">
                                                    {ticket.ticketTypeId?.name || (isOfferTicket ? 'Offer Ticket' : 'Standard')}
                                                </td>
                                                <td className="px-6 py-4 text-slate-600">
                                                    <div className="flex flex-col">
                                                        <span className="font-medium">{ticket.attendeeId?.name || 'Unknown'}</span>
                                                        {ticket.attendeeId?.email && (
                                                            <span className="text-xs text-slate-400">{ticket.attendeeId.email}</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${ticket.status === 'ACTIVE' ? 'bg-green-50 text-green-700 border-green-200' :
                                                            ticket.status === 'USED' ? 'bg-slate-100 text-slate-600 border-slate-200' :
                                                                'bg-red-50 text-red-700 border-red-200'
                                                        }`}>
                                                        {ticket.status}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 text-right">
                                                    <Link
                                                        to={`/admin/tickets/${ticket._id}`}
                                                        className="text-cyan-600 hover:text-cyan-800 font-medium hover:underline"
                                                    >
                                                        Details
                                                    </Link>
                                                </td>
                                            </tr>
                                        )
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AdminTickets
